import axios, { AxiosResponse } from 'axios';

import { GET_BULK_UPLOAD_STATUS, GET_USERS } from '@app/constants/endpoints';
import {
  getAccessToken,
  getOktaAccessToken,
  shouldRenewAccessToken,
} from '@app/helpers/localStorage';
import { formatRelativeTime } from '@app/i18n/formatters/relative-time';
import { buildPaginatedQueryString } from '@app/services/helpers';
import { IN_TESTING_MODE, TEST_AUTH_TOKEN } from '@app/tests/constants';
import type { User } from '@app/types/api/user';

export async function fetchOperators(
  filters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    exposureSkills?: string[];
    leadsOfGroups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    includeGroupDetails?: boolean;
    includeParentGroups?: boolean;
    limit?: number;
  },
  pageParam = 1,
) {
  const defaultFilters = {
    limit: 32,
    role: 'operator',
    status: 'active',
    sort: 'profile.firstName',
    order: 'ASC',
  };

  const finalFilters = {
    ...defaultFilters,
    ...filters,
  };

  if (finalFilters.ids && !finalFilters.ids.length) {
    return { operators: [] };
  }

  if (Array.isArray(finalFilters.groups) && finalFilters.groups.length === 0) {
    return { operators: [] };
  }

  if (
    Array.isArray(finalFilters.leadsOfGroups) &&
    finalFilters.leadsOfGroups.length === 0
  ) {
    return { operators: [] };
  }

  if (finalFilters.searchText) {
    finalFilters.sort = 'relevance';
    finalFilters.limit = 100;
  }

  const filterQueryString = buildPaginatedQueryString(pageParam, finalFilters);
  const url = `${GET_USERS}${filterQueryString ? `?${filterQueryString}` : ''}`;

  let token = TEST_AUTH_TOKEN;
  if (!IN_TESTING_MODE) {
    // Do NOT move this ternary into a helper function
    // because nested Promises with Redux dispatch can delay API calls.
    // Safe to refactor only after migrating away from Redux.
    token = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  return axios({
    url,
    method: 'GET',
    headers: { Authorization: token },
  }).then((response: AxiosResponse) => ({
    operators: response.data.users.map((operator: User) => ({
      ...operator,
      ...operator.profile,
      id: operator._id,
      name: `${operator?.profile?.firstName} ${operator?.profile?.lastName}`,
      imageUrl: operator?.profile?.imageUrl,
      lastLoginTime: operator.lastLoginTime
        ? formatRelativeTime(operator.lastLoginTime)
        : null,
      archived: operator.active !== undefined ? !operator.active : false,
    })),
  }));
}

export async function fetchBulkUploadStatus(): Promise<{
  inProgress: boolean;
}> {
  let token = TEST_AUTH_TOKEN;
  if (!IN_TESTING_MODE) {
    token = shouldRenewAccessToken()
      ? await getAccessToken()
      : getOktaAccessToken();
  }

  const response = await axios({
    url: GET_BULK_UPLOAD_STATUS,
    method: 'GET',
    headers: { Authorization: token },
  });
  return response.data;
}

export default fetchOperators;
